import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../Components/Layout";
import styles from "../styles/Elements.module.css";
import { elementsZodiac } from "../data/Elements-Zodiac";
import y2026 from "../data/yearly/2026.json";
import { useAuth } from "../auth/AuthContext";

const ElementZodiacPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open2026, setOpen2026] = useState(false);

  if (!slug) return <p>Loading...</p>;

  const parts = String(slug).split("-").filter(Boolean);
  const element = (parts[0] || "").toLowerCase();
  const animal = parts.slice(1).join("-").toLowerCase();

  const elementData = elementsZodiac.elements.find(
    (el) => el.element.toLowerCase() === element
  );

  if (!elementData) {
    return (
      <Layout title="Not Found">
       <p>No data found for element: {element}</p>
Back to{" "}
<Link to="/" title="My Chinese Horoscope - Find Your Zodiac Sign">
  My Chinese Horoscope
</Link>
      </Layout>
    );
  }

  const zodiacData = elementData.zodiacs.find(
    (z) => z.animal.toLowerCase() === animal
  );

  if (!zodiacData) {
    return (
      <Layout title="Not Found">
        <p>
          No data found for animal: {animal} in element: {element}
        </p>
        <Link to="/">Back to home</Link>
      </Layout>
    );
  }

  const yearlyKey = `${element}-${animal}`;
  const yearly = y2026?.[yearlyKey];

  return (
    <Layout
      title={`Chinese Horoscope ${elementData.element} ${zodiacData.animal} Zodiac Sign - Traits, Predictions & Compatibility | MyChineseHoroscope`}
      description={`Discover your ${elementData.element} ${zodiacData.animal} personality, love compatibility, career luck, and 2024-2025 predictions. Find your Chinese zodiac sign now!`}
      keywords={`Chinese horoscope, ${zodiacData.animal} zodiac, ${elementData.element} ${zodiacData.animal}, Chinese astrology finder, zodiac calculator, ${zodiacData.animal} traits, ${zodiacData.animal} compatibility`}
      schemaType="Article"
      schemaData={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: `${elementData.element} ${zodiacData.animal} Chinese Zodiac: Complete Guide`,
        description: `In-depth analysis of ${elementData.element} ${zodiacData.animal} traits, predictions, and compatibility in Chinese astrology.`,
        author: {
          "@type": "Organization",
          name: "MyChineseHoroscope",
          url: "https://mychinesehoroscope.com",
        },
        datePublished: "2025-08-05",
        dateModified: new Date().toISOString().split("T")[0],
        image: zodiacData.image,
        publisher: {
          "@type": "Organization",
          name: "MyChineseHoroscope",
          logo: {
            "@type": "ImageObject",
            url: "https://mychinesehoroscope.com/chinesehoroscope.png",
          },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://mychinesehoroscope.com/five-elements-zodiac/${slug}`,
        },
      }}
    >
      <div className={styles.container}>
        <h1>
          Chinese Horoscope Zodiac:{" "}
          {`${elementData.element.charAt(0).toUpperCase() + elementData.element.slice(1)} ${
            zodiacData.animal
          }`}
        </h1>
        <h2>All About the Chinese Horoscope Zodiac Sign</h2>

        <div className={styles.centeredImage}>
          <img
            src={`/five-elements-images/${elementData.element.toLowerCase()}-element.png`}
            alt={`${elementData.element} Element`}
            width={300}
            height={300}
            className={styles.elementImage}
          />
          <img
            src={zodiacData.image}
            alt={`${zodiacData.animal} Zodiac Sign`}
            width={300}
            height={300}
            className={styles.animalImage}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
          <button
            type="button"
            onClick={() => {
              if (!user) {
                navigate("/subscribe");
                return;
              }
              setOpen2026(true);
            }}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "#9c0000",
              cursor: "pointer",
              fontWeight: 800,
              color: "#fff",
            }}
          >
            View 2026 Reading
          </button>
        </div>

        <div className={styles.row}>
          <div className={styles.columns}>
            <h3>Strengths</h3>
            <p>{zodiacData.strengths}</p>
          </div>
          <div className={styles.columns}>
            <h3>Challenges</h3>
            <p>{zodiacData.challenges}</p>
          </div>
        </div>

        {zodiacData.career_work && (
          <div className={styles.row}>
            <div className={styles.columns}>
              <h3>Best Careers</h3>
              <p>{zodiacData.career_work.best_careers}</p>
            </div>
            <div className={styles.columns}>
              <h3>Work Challenges</h3>
              <p>{zodiacData.career_work.work_challenges}</p>
            </div>
          </div>
        )}

        {zodiacData.wealth_finances && (
          <div className={styles.block}>
            <h3>Wealth & Finances</h3>
            <p>{zodiacData.wealth_finances.description}</p>
            <div className={styles.advice}>
              <img
                src="/images/quote.png"
                alt="Quote Icon"
                className={styles.quoteIcon}
                loading="lazy"
              />
              <p className={`${styles["advice-text"]} ${styles.italicText}`}>
                {zodiacData.wealth_finances.financial_advice}
              </p>
            </div>
          </div>
        )}

        {zodiacData.health_wellbeing && (
          <div className={styles.block}>
            <h3>Health & Well-being</h3>
            <p>{zodiacData.health_wellbeing.description}</p>
            <div className={styles.advice}>
              <img
                src="/images/quote.png"
                alt="Quote Icon"
                className={styles.quoteIcon}
                loading="lazy"
              />
              <p className={`${styles["advice-text"]} ${styles.italicText}`}>
                {zodiacData.health_wellbeing.health_advice}
              </p>
            </div>
          </div>
        )}

        {zodiacData.social_life && (
          <div className={styles.block}>
            <h3>Social Life</h3>
            <p>{zodiacData.social_life.description}</p>
            <div className={styles.advice}>
              <img
                src="/images/quote.png"
                alt="Quote Icon"
                className={styles.quoteIcon}
                loading="lazy"
              />
              <p className={`${styles["advice-text"]} ${styles.italicText}`}>
                {zodiacData.social_life.social_advice}
              </p>
            </div>
          </div>
        )}

        {zodiacData.study_learning && (
          <div className={styles.block}>
            <h3>Study & Learning</h3>
            <p>{zodiacData.study_learning.description}</p>
            <div className={styles.advice}>
              <img
                src="/images/quote.png"
                alt="Quote Icon"
                className={styles.quoteIcon}
                loading="lazy"
              />
              <p className={`${styles["advice-text"]} ${styles.italicText}`}>
                {zodiacData.study_learning.learning_advice}
              </p>
            </div>

     <div style={{ textAlign: "center", marginTop: 20 }}>
  <h3>Find Another Chinese Horoscope</h3>


    <p style={{ marginBottom: 12, lineHeight: 1.6 }}>
      Discover your Chinese horoscope zodiac sign and element to understand your
      personality, compatibility, wealth potential, career path, and life
      opportunities. Use our Chinese zodiac finder to explore deeper insights
      into your relationships, lucky elements, and future predictions based on
      your date of birth.
    </p>
  <div
    className={styles.horoscopeFinderBox}
    style={{ textAlign: "center", padding: "16px" }}
  >
    <a
      href="/"
      title="My Chinese Horoscope Finder"
      style={{
        display: "inline-block",
        padding: "14px 22px",
        fontSize: 16,
        fontWeight: 800,
        borderRadius: 12,
        background: "#f3e1c7",
        color: "#7a0000",
  
        textDecoration: "none",
      }}
    >
      My Chinese Horoscope Finder
    </a>
  </div>
</div>
          </div>
        )}
      </div>

      {open2026 && (
        <div
          onClick={() => setOpen2026(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(900px, 100%)",
              maxHeight: "85vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: 14,
              padding: 18,
              boxShadow: "0 30px 80px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, fontWeight: 800 }}>
                  2026 Reading
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, marginTop: 4 }}>
                  {yearly?.title || `${elementData.element} ${zodiacData.animal}`}
                </div>
              </div>
              <button
                onClick={() => setOpen2026(false)}
                style={{
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "#ecd760",
                  borderRadius: 10,
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 900,
                  height: 60,
                  width: 60,
                }}
              >
                ✕
              </button>
            </div>

            {!yearly ? (
              <div style={{ marginTop: 16, lineHeight: 1.6 }}>
                No 2026 reading found for key: <b>{yearlyKey}</b>
                <br />
                Your JSON must include an entry like: <b>"{yearlyKey}"</b>
              </div>
            ) : (
              <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
                <Section title="Overview" text={yearly.overview} />
                <Section title="Love" text={yearly.love} />
                <Section title="Career" text={yearly.career} />
                <Section title="Money" text={yearly.money} />
                <Section title="Health" text={yearly.health} />

                {(yearly.bestMonths || yearly.cautionMonths) && (
                  <div style={cardStyle}>
                    <div style={{ fontWeight: 900, marginBottom: 6 }}>Timing</div>
                    {yearly.bestMonths && (
                      <div style={{ marginBottom: 6 }}>
                        <b>Best months:</b> {yearly.bestMonths}
                      </div>
                    )}
                    {yearly.cautionMonths && (
                      <div>
                        <b>Caution months:</b> {yearly.cautionMonths}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

function Section({ title, text }) {
  if (!text) return null;
  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 900, marginBottom: 6 }}>{title}</div>
      <div style={{ lineHeight: 1.6 }}>{text}</div>
    </div>
  );
}

const cardStyle = {
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: 12,
  padding: 14,
  background: "#fff",
};

export default ElementZodiacPage;